import { createAsyncThunk } from '@reduxjs/toolkit';
import { AbsenceActions } from '../../../constants/actions';
import { AxiosError } from 'axios';
import { AbsenceCreationDto, AbsenceUpdateDto } from '@docbay/schemas';
import { AbsenceApi } from '../../../../integration/api/professionalAbsences';

/**
 * Asynchronously creates a new absence.
 *
 * @param data - The absence creation details to be used.
 * @returns A promise that resolves with the created absence data or rejects with an error message.
 */
export const createAbsence = createAsyncThunk(
  AbsenceActions.createAbsence,
  async (data: AbsenceCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AbsenceApi.createAbsence(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously deletes an absence.
 *
 * @param id - The ID of the absence to be deleted.
 * @returns A promise that resolves with the deleted absence data or rejects with an error message.
 */
export const deleteAbsence = createAsyncThunk(
  AbsenceActions.deleteAbsence,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AbsenceApi.deleteAbsence(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously retrieves an absence by its ID.
 *
 * @param id - The ID of the absence to be retrieved.
 * @returns A promise that resolves with the retrieved absence data or rejects with an error message.
 */
export const getAbsenceById = createAsyncThunk(
  AbsenceActions.getAbsenceById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await AbsenceApi.gteAbsenceById(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Asynchronously updates an absence.
 *
 * @param data - The updated absence details to be used.
 * @returns A promise that resolves with the updated absence data or rejects with an error message.
 */
export const updateAbsence = createAsyncThunk(
  AbsenceActions.updateAbsence,
  async (data: AbsenceUpdateDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AbsenceApi.updateAbsence(data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
